import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import storage from '../../lib/storage';
import AppLayout from '../../layouts/AppLayout';
import useManualEditor from './hooks/useManualEditor';
import CanvasStage from './components/CanvasStage';
import EditorToolbar from './components/EditorToolbar';
import InspectorPanel from './components/InspectorPanel';
import MediaPickerModal from './components/MediaPickerModal';
import IconPickerModal from './components/IconPickerModal';
import './styles.css';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ManualCreatePage() {
  const navigate = useNavigate();
  const uploadRef = useRef(null);
  const query = useQuery();
  const designId = query.get('designId');
  const fromCreative = query.get('fromCreative') === '1';
  const initialImageUrlParam = query.get('imageUrl');
  const [openMediaType, setOpenMediaType] = useState('');
  const [isIconModalOpen, setIsIconModalOpen] = useState(false);

  const creativeBootstrap = useMemo(() => {
    if (!fromCreative) return null;
    const payload = storage.get('mp_manual_editor_structure');
    if (!payload || typeof payload !== 'object') return null;
    return payload;
  }, [fromCreative]);

  const initialDesignPayload = useMemo(() => {
    const structure = creativeBootstrap?.structure;
    if (!structure || typeof structure !== 'object') return null;

    if (structure.type === 'design' && structure.canvas_data) {
      return {
        type: 'canvas-data',
        slides: [{ id: 'slide-1', canvas_data: structure.canvas_data }],
      };
    }

    if (structure.type === 'carousel' && Array.isArray(structure.slides)) {
      return {
        type: 'canvas-data',
        slides: structure.slides.map((slide) => ({
          id: slide?.id,
          canvas_data: slide?.canvas_data || null,
        })),
      };
    }

    return null;
  }, [creativeBootstrap]);

  const initialImageUrl = useMemo(() => {
    if (initialImageUrlParam) return initialImageUrlParam;
    const structure = creativeBootstrap?.structure;
    if (!structure || typeof structure !== 'object') return null;
    if (typeof structure.image_url === 'string' && structure.image_url) return structure.image_url;
    if (typeof structure.video_thumb === 'string' && structure.video_thumb) return structure.video_thumb;
    return null;
  }, [creativeBootstrap, initialImageUrlParam]);

  const {
    canvasElementRef,
    isReady,
    isLoading,
    isSaving,
    error,
    designTitle,
    setDesignTitle,
    layers,
    activeLayerId,
    activeObjectType,
    opacity,
    textStyle,
    applyTextStyle,
    videoSourceUrl,
    addShape,
    addText,
    addImageFromUrl,
    addVideoFromUrl,
    uploadImage,
    addMarketingLogo,
    addDefaultLogo,
    removeBackgroundSelected,
    bringForward,
    sendBackward,
    rotateSelected,
    removeSelected,
    setSelectedOpacity,
    selectLayer,
    saveCanvas,
    exportPng,
    downloadVideo,
  } = useManualEditor({ initialDesignId: designId, initialImageUrl, initialDesignPayload });

  return (
    <AppLayout
      title="Criação Manual"
      subtitle="Editor visual para montar artes com camadas, imagens e exportação PNG"
      fullWidth
    >
          <div className="manual-topbar">
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/contents')}>
              Ir para Conteúdos
            </button>
            <button className="btn btn-secondary" type="button" onClick={saveCanvas} disabled={!isReady || isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
            <button className="btn btn-primary" type="button" onClick={exportPng} disabled={!isReady}>
              Exportar PNG
            </button>
          </div>

          <div className="manual-editor-v2">
            <EditorToolbar
              onAddText={addText}
              onAddShape={addShape}
              onUploadClick={() => uploadRef.current?.click()}
              onOpenImagePicker={() => setOpenMediaType('photo')}
              onOpenVideoPicker={() => setOpenMediaType('video')}
              onOpenIconPicker={() => setIsIconModalOpen(true)}
              onRemoveBackground={removeBackgroundSelected}
              onRotateLeft={() => rotateSelected(-15)}
              onRotateRight={() => rotateSelected(15)}
              onBringForward={bringForward}
              onSendBackward={sendBackward}
              onDelete={removeSelected}
              onSave={saveCanvas}
              onExport={exportPng}
            />

            <CanvasStage canvasElementRef={canvasElementRef} />

            <InspectorPanel
              title={designTitle}
              onTitleChange={setDesignTitle}
              layers={layers}
              activeLayerId={activeLayerId}
              onSelectLayer={selectLayer}
              textStyle={textStyle}
              onApplyTextStyle={applyTextStyle}
              opacity={opacity}
              onOpacityChange={setSelectedOpacity}
              activeObjectType={activeObjectType}
              onAddColorLogo={addMarketingLogo}
              onAddWhiteLogo={addDefaultLogo}
              videoSourceUrl={videoSourceUrl}
              onDownloadVideo={downloadVideo}
            />
          </div>

          {(isLoading || error) && <p className="manual-error">{isLoading ? 'Carregando arte...' : error}</p>}

          <input
            ref={uploadRef}
            className="manual-hidden-upload"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={(event) => uploadImage(event.target.files?.[0])}
          />

          <MediaPickerModal
            open={Boolean(openMediaType)}
            type={openMediaType || 'photo'}
            onClose={() => setOpenMediaType('')}
            onSelectImage={(url) => addImageFromUrl(url, 'photo')}
            onSelectVideo={(url) => addVideoFromUrl(url)}
          />

          <IconPickerModal
            open={isIconModalOpen}
            onClose={() => setIsIconModalOpen(false)}
            onSelectIcon={(url) => addImageFromUrl(url, 'icon')}
          />
    </AppLayout>
  );
}
